import { useQuery } from '@tanstack/react-query';
import { apiClient } from '@/lib/apiClient';
import { ErrorService } from '@/lib/errors/ErrorService';
import { MemoryUsageSchema, type MemoryUsage } from '@hector/shared-types/casediscovery';

/** Query key racine de la jauge persistée — exportée pour l'invalidation. */
export const V3_CONVERSATION_MEMORY_USAGE_KEY = 'v3-conversation-memory-usage';

/**
 * Jauge de mémoire PERSISTÉE d'une conversation (dernier comptage du MasterLoop,
 * `casediscovery_conversations.context_tokens_used`).
 *
 * WHY : la jauge live n'arrive que par WebSocket pendant un tour ; une conversation
 * rouverte n'a pas de session live et son rond restait vide. L'appelant doit
 * préférer la valeur live (`session.memoryUsage ?? persisted`) : elle est plus
 * récente dès qu'un tour a tourné dans cet onglet.
 *
 * Best-effort : échec ou 404 → null, jamais de toast — le rond affiche alors
 * « Mesurée au prochain échange », comme avant.
 *
 * @param conversationId - La conversation affichée, ou null
 * @param options.skipConversationId - Conversation créée localement à l'instant :
 *   pas encore persistée, donc jamais mesurée. Même contrat que `useConversationHistory`.
 */
export function useConversationMemoryUsage(
  conversationId: string | null,
  options?: { skipConversationId?: string | null },
): MemoryUsage | null {
  const skip = !!conversationId && options?.skipConversationId === conversationId;

  const query = useQuery({
    queryKey: [V3_CONVERSATION_MEMORY_USAGE_KEY, conversationId],
    enabled: !!conversationId && !skip,
    // Dédoublonne les remontages du chat (transitions Canvas) sans figer la valeur :
    // la fin de session live invalide explicitement cette clé (`sessionDoneBridge`).
    staleTime: 30_000,
    retry: false,
    queryFn: async (): Promise<MemoryUsage | null> => {
      try {
        const raw = await apiClient.get<unknown>(
          `/case-discovery-v3/conversations/${conversationId as string}/memory-usage`,
        );
        // Frontière système : un backend d'une autre version (Vercel part avant Render) ne
        // doit jamais faire afficher un pourcentage fabriqué depuis une forme inattendue.
        const parsed = MemoryUsageSchema.nullable().safeParse(raw);
        if (!parsed.success) {
          throw new Error(
            `GET /case-discovery-v3/conversations/${conversationId as string}/memory-usage returned a payload that does not match MemoryUsageSchema: ${parsed.error.message}`,
          );
        }
        return parsed.data;
      } catch (error) {
        ErrorService.handle(error, {
          context: { action: `load_conversation_memory_usage:${conversationId as string}` },
          silent: true,
        });
        return null;
      }
    },
  });

  return query.data ?? null;
}
